import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Grid, Box } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import FormRadioStack from '../../../Form/FormRadioStack';
import FormTextField from '../../../Form/FormTextField';
import FormSelect from '../../../Form/FormSelect';
import DbeDatePicker from '../../../Inputs/DbeDatePicker';

const EntityGeneralitiesPage = function EntityGeneralitiesPage({
  errors,
  data,
  formData,
  contact = false,
  edit = false,
  contactTypesOptions = [],
  isLongChannel = false,
}) {
  const intl = useIntl();
  const { reset, control, setValue, watch } = useFormContext();
  const selectedContactTypeId = watch('contactTypeId');

  useEffect(() => {
    if (typeof data !== 'boolean' && Object.keys(data || {}).length > 0) {
      reset({
        ...(data ?? {}),
        ...(formData ?? {}),
      });
    }
  }, [data]);

  const filteredContactTypes = useMemo(() => {
    return contactTypesOptions?.filter(
      ({ id }) => !contact || data?.contactTypeId === 1 || (contact && id !== 1)
    );
  }, [contactTypesOptions, contact, data?.contactTypeId]);

  useEffect(() => {
    if (!selectedContactTypeId && filteredContactTypes?.length > 0) {
      setValue('contactTypeId', filteredContactTypes[0].id, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [filteredContactTypes, selectedContactTypeId, setValue]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 4, sm: 6, md: 4 }}>
        <FormSelect
          name="contactTypeId"
          label={intl.messages.CONTACT_TYPE}
          required
          options={filteredContactTypes.map(({ id, contactType }) => ({
            label: contactType,
            value: id,
          }))}
          disabled={
            (!edit && !contact) || //if in customer creation mode, disabled
            (contact && edit && data?.contactTypeId === 1) //in contact edit mode, if the contact is a primary contact, disabled
          }
          defaultValue={filteredContactTypes[0]?.id}
          error={errors?.contactTypeId}
        />
      </Grid>
      {!contact && (
        <>
          <Grid size={{ md: 4, sm: 6, xs: 12 }}>
            <FormTextField
              allUpperCase
              name="customerCode"
              error={errors?.customerCode}
            />
          </Grid>
          {isLongChannel && (
            <Grid size={{ md: 4, sm: 6, xs: 12 }}>
              <FormTextField
                allUpperCase
                name="companyName" //ragione sociale
                label={intl.messages.COMPANY_NAME}
                error={errors?.companyName}
              />
            </Grid>
          )}
          <Grid size={{ md: 4, sm: 6, xs: 12 }}>
            <FormTextField
              name="vatCode"
              error={errors?.vatCode}
              maxCharacters={11}
              validateCallback={(value) => {
                if (!value) return true;
                return (/^[0-9]{11}$/.test(value) || intl.messages.INVALID_VATCODE);
              }}
            />
          </Grid>
        </>
      )}
      <Grid size={{ md: 4, sm: 6, xs: 12 }}>
        <FormTextField
          required
          allUpperCase
          name="firstName"
          error={errors?.firstName}
        />
      </Grid>
      <Grid size={{ md: 4, sm: 6, xs: 12 }}>
        <FormTextField
          allUpperCase
          name="lastName"
          required
          error={errors?.lastName}
        />
      </Grid>
      <Grid size={{ md: 4, sm: 6, xs: 12 }}>
        <FormTextField
          allUpperCase
          name="middleName"
          error={errors?.middleName}
        />
      </Grid>
      <Grid size={{ md: 4, sm: 6, xs: 12 }}>
        <Box sx={{ marginLeft: '10px' }}>
          <FormRadioStack
            defaultValue={
              data?.gender ? (data?.gender === 'Female' ? 'F' : 'M') : null
            }
            name="gender"
            options={['M', 'F']}
            row
            error={errors?.gender}
          />
        </Box>
      </Grid>
      <Grid size={{ md: 4, sm: 6, xs: 12 }}>
        <Controller
          name="birthDate"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DbeDatePicker
              date={value}
              onChange={onChange}
              label={intl.messages.BIRTH_DATE}
            />
          )}
        />
      </Grid>
      <Grid size={{ md: 4, sm: 6, xs: 12 }}>
        <FormTextField
          name="fiscalCode"
          error={errors?.fiscalCode}
          allUpperCase
          maxCharacters={16}
          validateCallback={(cf) => {
            if (!cf) return true;
            return cf.length === 16 ? true : intl.messages.CF_ERROR;
          }}
        />
      </Grid>
    </Grid>
  );
};

EntityGeneralitiesPage.propTypes = {
  errors: PropTypes.object,
  data: PropTypes.object,
  // formData: PropTypes.object,
};

export default EntityGeneralitiesPage;
